/**
 * Home-currency doctrine for the company ledger.
 *
 * One home currency per company. Ledger totals never mix codes. A cost entered
 * in another currency must be converted with an **explicit** FX rate — the desk
 * does not invent a market rate. Store the home amount plus optional original /
 * FX note. Grok / persona must not invent ledger posts.
 *
 * Operator-sim economics — not GAAP / TC audit.
 */

import type { CompanyAccount, LedgerTxn } from "./types";
import { DEFAULT_CURRENCY, hydrateCurrency, isCurrencyCode, type CurrencyCode } from "./catalogs";

function isOpeningLedgerLine(txn: Pick<LedgerTxn, "kind" | "memo">): boolean {
  if (txn.kind !== "adjustment") return false;
  return /opening company cash|opening balance adjustment/i.test(txn.memo ?? "");
}

export { DEFAULT_CURRENCY, hydrateCurrency, isCurrencyCode };
export type { CurrencyCode };

export const HOME_CURRENCY_HELP =
  "One home currency for this ledger (CAD default for Northstar ops). Totals never mix codes. A foreign amount needs an explicit FX rate into home — stored as the home figure plus the original / rate note. Switching home after ops posts also needs an explicit rate. Classic math owns money; Grok does not invent ledger posts.";

export type FxConversion = {
  originalAmount: number;
  originalCurrency: CurrencyCode;
  homeCurrency: CurrencyCode;
  fxRate: number;
  note?: string;
};

export type ConvertToHomeOk = {
  ok: true;
  homeAmount: number;
  homeCurrency: CurrencyCode;
  fx?: FxConversion;
};

export type ConvertToHomeErr = {
  ok: false;
  error: string;
};

export function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function homeCurrencyOf(account: Pick<CompanyAccount, "homeCurrency"> | undefined): CurrencyCode {
  return hydrateCurrency(account?.homeCurrency);
}

/**
 * Convert an amount into home currency. Same-code is a no-op.
 * Different code requires an explicit rate (home units per 1 original unit).
 */
export function convertToHome(input: {
  amount: number;
  currency: CurrencyCode;
  homeCurrency: CurrencyCode;
  fxRate?: number;
  fxNote?: string;
}): ConvertToHomeOk | ConvertToHomeErr {
  const amount = Number(input.amount);
  if (!Number.isFinite(amount)) {
    return { ok: false, error: "Amount is not a number." };
  }
  const from = hydrateCurrency(input.currency);
  const home = hydrateCurrency(input.homeCurrency);
  if (from === home) {
    return { ok: true, homeAmount: roundMoney(amount), homeCurrency: home };
  }
  const rate = Number(input.fxRate);
  if (!Number.isFinite(rate) || rate <= 0) {
    return {
      ok: false,
      error: `Explicit FX rate required to convert ${from} into home ${home}. Ledger totals do not mix currencies.`,
    };
  }
  return {
    ok: true,
    homeAmount: roundMoney(amount * rate),
    homeCurrency: home,
    fx: {
      originalAmount: roundMoney(amount),
      originalCurrency: from,
      homeCurrency: home,
      fxRate: rate,
      note: input.fxNote?.trim() || `${from} → ${home} @ ${rate}`,
    },
  };
}

export function txnHomeCurrency(txn: Pick<LedgerTxn, "currency">, home: CurrencyCode): CurrencyCode {
  return hydrateCurrency(txn.currency ?? home);
}

/** True when a line is already in home (or has no code — treated as home). */
export function txnIsHome(txn: Pick<LedgerTxn, "currency">, home: CurrencyCode): boolean {
  return txnHomeCurrency(txn, home) === home;
}

export function mixedCurrencyKeys(
  account: Pick<CompanyAccount, "homeCurrency" | "transactions">,
): string[] {
  const home = homeCurrencyOf(account);
  return account.transactions
    .filter((t) => t.currency && t.currency !== home)
    .map((t) => t.id);
}

/**
 * Ledger totals only sum home-currency lines. Foreign lines without conversion
 * are excluded — they must not be added into the cash figure.
 */
export function homeLedgerTotal(account: CompanyAccount): { balance: number; currency: CurrencyCode; excluded: number } {
  const currency = homeCurrencyOf(account);
  const homeLines = account.transactions.filter((t) => txnIsHome(t, currency));
  const balance = roundMoney(homeLines.reduce((s, t) => s + t.amountCad, 0));
  return {
    balance,
    currency,
    excluded: account.transactions.length - homeLines.length,
  };
}

export function needsExplicitFxToChangeHome(
  account: CompanyAccount,
  next: CurrencyCode,
): boolean {
  if (homeCurrencyOf(account) === hydrateCurrency(next)) return false;
  return account.transactions.some((t) => !isOpeningLedgerLine(t));
}

export type HomeCurrencyChange =
  | { ok: true; account: CompanyAccount; converted: boolean }
  | { ok: false; error: string };

/**
 * Change the ledger unit of account.
 * Opening-only books may relabel (same numbers — capital was typed in that unit).
 * After ops posts, an explicit FX rate converts every home line.
 */
export function applyHomeCurrencyChange(
  account: CompanyAccount,
  nextRaw: CurrencyCode,
  fx?: { rate: number; note?: string },
): HomeCurrencyChange {
  const next = hydrateCurrency(nextRaw);
  const prev = homeCurrencyOf(account);
  if (next === prev) {
    return { ok: true, account: { ...account, homeCurrency: next }, converted: false };
  }
  if (needsExplicitFxToChangeHome(account, next)) {
    const converted = convertToHome({
      amount: 1,
      currency: prev,
      homeCurrency: next,
      fxRate: fx?.rate,
      fxNote: fx?.note,
    });
    if (!converted.ok) return converted;
    const rate = converted.fx!.fxRate;
    const note = converted.fx!.note;
    const transactions = account.transactions.map((t) => {
      if (!txnIsHome(t, prev)) return t;
      return {
        ...t,
        amountCad: roundMoney(t.amountCad * rate),
        currency: next,
        originalAmount: t.originalAmount ?? t.amountCad,
        originalCurrency: t.originalCurrency ?? prev,
        fxRate: rate,
        fxNote: note,
      };
    });
    return {
      ok: true,
      converted: true,
      account: {
        homeCurrency: next,
        transactions,
        balanceCad: roundMoney(account.balanceCad * rate),
      },
    };
  }
  return {
    ok: true,
    converted: false,
    account: {
      ...account,
      homeCurrency: next,
      transactions: account.transactions.map((t) =>
        txnIsHome(t, prev) ? { ...t, currency: next } : t,
      ),
    },
  };
}

export function fxFieldsFromConversion(fx?: FxConversion): Pick<
  LedgerTxn,
  "originalAmount" | "originalCurrency" | "fxRate" | "fxNote"
> {
  if (!fx) return {};
  return {
    originalAmount: fx.originalAmount,
    originalCurrency: fx.originalCurrency,
    fxRate: fx.fxRate,
    fxNote: fx.note,
  };
}

export function isSupportedCurrency(value: unknown): value is CurrencyCode {
  return isCurrencyCode(value);
}
